/**
 * Studio lead tracking — LIN-278 (theme), LIN-277 (server).
 *
 * Loaded on every page (layout/theme.liquid), because:
 *  - campaign parameters (utm_*, fbclid, gclid) are on the LANDING page, which is rarely the
 *    product page the shopper eventually taps WhatsApp on — they are kept for 30 days;
 *  - the site-wide floating WhatsApp button is tracked too, as the baseline outside the
 *    studio-first PDP.
 *
 * For every studio tap it fans out three ways, sharing one `event_id`:
 *  1. dataLayer  → GTM → GA4                     (studio_* events, docs/studio-pdp-tracking.md)
 *  2. fbq        → Meta browser pixel           (Contact / FindLocation)
 *  3. sendBeacon → veridian /api/studio-lead    (StudioLead row + Meta CAPI twin, deduped on event_id)
 *
 * Events come from assets/studio-cta.js as `studio:view` / `studio:action` on document.
 *
 * Consent (Shopify Customer Privacy API, the site's cookie banner):
 *  - analytics explicitly declined → no lead beacon (GTM applies its own consent rules);
 *  - marketing explicitly declined → no fbq, and the server skips CAPI.
 *  No decision yet (banner ignored) or no API is treated as allowed.
 *
 * Testing on a preview theme: add `?studio_capi_test=TEST12345` (the code from Meta Events
 * Manager → Test Events) once; it is kept for the tab session and routes the server events to
 * Test Events instead of real attribution.
 */
(function () {
  'use strict';
  if (window.NoritamyStudioTracking) return;

  var LEAD_ENDPOINT = 'https://veridian-flow-portal.vercel.app/api/studio-lead';
  var VERSION = 'v1_studio_pdp';
  var TOUCH_KEY = 'nr_touch';
  var TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var TEST_KEY = 'nr_studio_capi_test';
  var TOUCH_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];

  /* ------------------------------------------------------------ storage */

  function safe(fn, fallback) {
    try { return fn(); } catch (e) { return fallback; }
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  /** Last-touch campaign params: overwritten whenever a landing URL carries any of them. */
  function captureTouch() {
    var params = new URLSearchParams(window.location.search);
    var touch = {};
    var any = false;
    TOUCH_PARAMS.forEach(function (k) {
      var v = params.get(k);
      if (v) { touch[k] = v.slice(0, 500); any = true; }
    });
    if (any) {
      touch.ts = Date.now();
      safe(function () { localStorage.setItem(TOUCH_KEY, JSON.stringify(touch)); });
    }
    var test = params.get('studio_capi_test');
    if (test) safe(function () { sessionStorage.setItem(TEST_KEY, test); });
  }

  function readTouch() {
    return safe(function () {
      var t = JSON.parse(localStorage.getItem(TOUCH_KEY) || 'null');
      return t && Date.now() - t.ts < TOUCH_TTL_MS ? t : {};
    }, {}) || {};
  }

  /* ---------------------------------------------------------- identity */

  /**
   * true = accepted, false = explicitly declined, null = no decision yet.
   *
   * Not analyticsProcessingAllowed() / marketingAllowed(): in Israel (region ILTA) they return
   * false until the visitor clicks the banner, which would silently drop every shopper who
   * ignores it. currentVisitorConsent() tells 'yes' / 'no' / '' (undecided) apart.
   */
  function consent() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    var current = cp && typeof cp.currentVisitorConsent === 'function'
      ? safe(function () { return cp.currentVisitorConsent(); }, null)
      : null;
    var read = function (v) { return v === 'yes' ? true : v === 'no' ? false : null; };
    return current ? { analytics: read(current.analytics), marketing: read(current.marketing) } : { analytics: null, marketing: null };
  }

  function gaClientId() {
    var ga = cookie('_ga'); // GA1.1.1234567890.1690000000
    return ga ? ga.split('.').slice(-2).join('.') : null;
  }

  function gclid(touch) {
    if (touch.gclid) return touch.gclid;
    var aw = cookie('_gcl_aw'); // GCL.1690000000.<gclid>
    return aw ? aw.split('.').slice(2).join('.') || null : null;
  }

  function fbc(touch) {
    var c = cookie('_fbc');
    if (c) return c;
    return touch.fbclid ? 'fb.1.' + touch.ts + '.' + touch.fbclid : null;
  }

  function newEventId() {
    var rand = safe(function () {
      var a = new Uint32Array(2);
      crypto.getRandomValues(a);
      return a[0].toString(36) + a[1].toString(36);
    }, Math.random().toString(36).slice(2));
    return 'st_' + Date.now().toString(36) + rand;
  }

  /* ------------------------------------------------------------- sinks */

  var DL_EVENT = {
    'view:buybox': 'studio_module_view',
    'view:sticky': 'studio_sticky_shown',
    'action:whatsapp': 'studio_whatsapp_click',
    'action:directions': 'studio_directions_click',
    'action:open_nav': 'studio_nav_open',
  };
  var META_EVENT = { whatsapp: 'Contact', directions: 'FindLocation' };

  function toDataLayer(eventName, d, eventId) {
    window.dataLayer = window.dataLayer || [];
    // item_id = variant id, the key of the Meta / Google Merchant catalogs (Multifeed).
    var catalogId = d.catalog_variant_id || d.variant_id || d.product_id;
    var item = catalogId
      ? {
          item_id: String(catalogId),
          item_name: d.product_title || undefined,
          item_variant: d.variant_title || undefined,
          price: d.price != null ? d.price : undefined,
          quantity: 1,
        }
      : null;
    // Null reset first so item data never bleeds in from the previous ecommerce push.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      feature_version: VERSION,
      event_id: eventId,
      studio_placement: d.placement || null,
      studio_provider: d.provider || null,
      pdp_mode: d.pdp_mode || null,
      open_now: d.open_now == null ? null : d.open_now,
      // Event-level copies: GA4's Data API cannot break custom events down by item fields.
      // Prefixed (like tile_product_id) so they never collide with GA4's ecommerce item_id.
      studio_product_id: d.product_id ? String(d.product_id) : null,
      studio_variant_id: d.variant_id || null,
      value: d.price != null ? d.price : null,
      currency: d.currency || 'ILS',
      ecommerce: item ? { currency: d.currency || 'ILS', value: d.price, items: [item] } : undefined,
    });
  }

  function toMeta(action, d, eventId, c) {
    if (c.marketing === false || typeof window.fbq !== 'function' || !META_EVENT[action]) return;
    var data = { content_type: 'product', placement: d.placement, pdp_mode: d.pdp_mode || undefined };
    // Catalog items are variants (Multifeed), so content_ids must be variant ids to match.
    var catalogId = d.catalog_variant_id || d.variant_id || d.product_id;
    if (catalogId) data.content_ids = [String(catalogId)];
    if (d.product_title) data.content_name = d.product_title;
    if (d.price != null) { data.value = d.price; data.currency = d.currency || 'ILS'; }
    if (d.provider) data.provider = d.provider;
    safe(function () { window.fbq('track', META_EVENT[action], data, { eventID: eventId }); });
  }

  function toLeadEndpoint(action, d, eventId, c) {
    if (c.analytics === false || !META_EVENT[action]) return;
    var shop = window.Shopify && window.Shopify.shop;
    if (!shop) return;
    var touch = readTouch();
    var payload = {
      event_id: eventId,
      shop: shop,
      action: action,
      provider: d.provider || null,
      placement: d.placement || 'unknown',
      pdp_mode: d.pdp_mode || null,
      product_id: d.product_id ? String(d.product_id) : null,
      variant_id: d.variant_id ? String(d.variant_id) : null,
      catalog_variant_id: d.catalog_variant_id ? String(d.catalog_variant_id) : null,
      variant_title: d.variant_title || null,
      product_title: d.product_title || null,
      price: typeof d.price === 'number' ? d.price : null,
      currency: d.currency || 'ILS',
      open_now: typeof d.open_now === 'boolean' ? d.open_now : null,
      page_url: window.location.href.slice(0, 2000),
      referrer: document.referrer ? document.referrer.slice(0, 2000) : null,
      utm_source: touch.utm_source || null,
      utm_medium: touch.utm_medium || null,
      utm_campaign: touch.utm_campaign || null,
      utm_content: touch.utm_content || null,
      utm_term: touch.utm_term || null,
      fbclid: touch.fbclid || null,
      fbc: fbc(touch),
      fbp: cookie('_fbp'),
      gclid: gclid(touch),
      ga_client_id: gaClientId(),
      shopify_client_id: cookie('_shopify_y'),
      consent_marketing: c.marketing,
      test_event_code: safe(function () { return sessionStorage.getItem(TEST_KEY); }, null),
    };
    var body = JSON.stringify(payload);
    // text/plain keeps it a CORS "simple" request — no preflight racing the WhatsApp navigation.
    var sent = safe(function () {
      return navigator.sendBeacon(LEAD_ENDPOINT, new Blob([body], { type: 'text/plain' }));
    }, false);
    if (!sent) {
      safe(function () {
        fetch(LEAD_ENDPOINT, { method: 'POST', body: body, keepalive: true, headers: { 'Content-Type': 'text/plain' } });
      });
    }
  }

  function track(kind, d) {
    var key = kind === 'view' ? 'view:' + d.placement : 'action:' + d.action;
    var dlEvent = DL_EVENT[key];
    if (!dlEvent) return;
    var eventId = newEventId();
    var c = consent();
    toDataLayer(dlEvent, d, eventId);
    if (kind === 'action') {
      toMeta(d.action, d, eventId, c);
      toLeadEndpoint(d.action, d, eventId, c);
    }
  }

  /* ----------------------------------------------------------- sources */

  document.addEventListener('studio:view', function (e) { track('view', e.detail || {}); });
  document.addEventListener('studio:action', function (e) { track('action', e.detail || {}); });

  // The site-wide floating WhatsApp button (snippets/floating-btn.liquid). Hidden on
  // studio-first products, so every tap here is the non-studio baseline.
  document.addEventListener('click', function (e) {
    var btn = e.target instanceof Element && e.target.closest('.floating__btn');
    if (!btn) return;
    var meta = safe(function () { return window.ShopifyAnalytics.meta; }, null) || {};
    var product = meta.product || null;
    var selectedId = meta.selectedVariantId;
    var variant = product && product.variants
      ? (product.variants.find(function (v) { return String(v.id) === String(selectedId); }) || product.variants[0])
      : null;
    track('action', {
      action: 'whatsapp',
      placement: 'floating',
      pdp_mode: product ? 'standard' : null,
      product_id: product ? product.id : null,
      catalog_variant_id: variant ? variant.id : null,
      product_title: variant ? variant.name : null,
      price: variant && typeof variant.price === 'number' ? variant.price / 100 : null,
      currency: meta.currency || 'ILS',
    });
  }, true);


  // WhatsApp links inside content pages (content-page WhatsApp) — e.g. /pages/annoory.
  // Fires the SAME action as the studio-first PDP ('whatsapp' -> GA4 studio_whatsapp_click,
  // Meta Contact, lead endpoint), so a tap here counts the same. The page handle goes in
  // `placement` so the source stays separable from a product page.
  document.addEventListener('click', function (e) {
    var link = e.target instanceof Element &&
      e.target.closest('a[href*="wa.me"], a[href*="api.whatsapp.com"]');
    if (!link) return;
    if (link.closest('.floating__btn, [data-studio-placement], [data-er-action]')) return; // already tracked
    var m = window.location.pathname.match(/\/pages\/([^/?#]+)/);
    if (!m) return;
    track('action', {
      action: 'whatsapp',
      placement: 'page_' + m[1],
      pdp_mode: 'content_page',
      product_id: null,
      product_title: document.title || null,
      currency: 'ILS',
    });
  }, true);

  captureTouch();
  window.NoritamyStudioTracking = { version: VERSION, readTouch: readTouch };
})();
